import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import PropTypes from "prop-types";

import { palette } from "~/theme";
import { link } from "~/styles/typographyStyles";
import { rotateCentered, searchReveal } from "~/styles/keyframes";

import { ScreenReaderText } from "~/styles/screenReaderText";
import FaIcon from "~/components/FaIcon";

const maybeAddListStyles = ({ isModal }) => {
    if (isModal) {
        return "";
    }
    return css`
        max-height: 300px;
        box-shadow: 0 3px 2px 0 rgba(0, 0, 0, 0.5);
    `;
};

const List = styled.ul`
    position: absolute;
    z-index: ${({ theme }) => theme.zIndex.header};
    width: 100%;
    overflow: scroll;
    padding-top: 2rem;
    animation: ${searchReveal} 0.25s ease-in;
    ${maybeAddListStyles};

    & li:last-child {
        padding-bottom: 2rem;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
        max-height: 300px;
        box-shadow: 0 3px 2px 0 rgba(0, 0, 0, 0.5);
    }
`;

const ListItem = styled.li`
    cursor: pointer;
    padding: 0.5rem 0 0.5rem 2rem;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
        padding: 0.5rem 0 0.5rem ${({ isModal }) => (isModal ? "5rem" : "4rem")};
    }
`;

const Suggestion = styled.span`
    ${link};
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.typography.links};
`;

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 2rem;
    position: absolute;
    left: 0;
    z-index: ${({ theme }) => theme.zIndex.stickyJumpLinks};
    color: ${({ theme }) => theme.palette.purple};
    min-height: 8rem;
    width: 100%;
    background-color: ${({ theme }) => theme.palette.white};
    padding-left: 4rem;
    font-weight: 800;

    & > span {
        margin-top: -2rem;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
        padding-left: ${({ isModal }) => (isModal ? "5rem" : "4rem")};
    }
`;

const LoadingIcon = styled(FaIcon)`
    animation: ${rotateCentered} 1s linear infinite normal forwards;
`;

const AutocompleteResults = ({
    loading,
    noMatches,
    inputValue,
    getMenuProps,
    getItemProps,
    highlightedIndex = null,
    selectedItem = null,
    isOpen,
    isModal = false,
    suggestions,
}) => {
    if (!isOpen || !inputValue) {
        return null;
    }

    if (loading || noMatches) {
        return (
            <Wrapper isModal={isModal}>
                {loading ? (
                    <>
                        <span>Loading...</span>
                        <LoadingIcon name="spinner" />
                    </>
                ) : null}
                {noMatches ? <span>No matches</span> : null}
            </Wrapper>
        );
    }

    return (
        <List isModal={isModal} {...getMenuProps()}>
            {suggestions.map((item, index) => (
                <ListItem
                    isModal={isModal}
                    {...getItemProps({
                        key: item.title + index,
                        index,
                        item,
                        style: {
                            backgroundColor:
                                highlightedIndex === index
                                    ? "lightgray"
                                    : "white",
                            outline:
                                highlightedIndex === index
                                    ? `5px solid ${palette.purple}`
                                    : "none",
                            outlineOffset: "-5px",
                            fontWeight:
                                selectedItem?.title === item.title
                                    ? "bold"
                                    : "normal",
                        },
                    })}
                >
                    <Suggestion>
                        <ScreenReaderText>{item.resourceType}</ScreenReaderText>
                        <span>{item.doc.getIcon()}</span>
                        <span>{item.title}</span>
                    </Suggestion>
                </ListItem>
            ))}
        </List>
    );
};

AutocompleteResults.propTypes = {
    highlightedIndex: PropTypes.number,
    selectedItem: PropTypes.shape({
        title: PropTypes.string,
        doc: PropTypes.object,
    }),
    isModal: PropTypes.bool,
    loading: PropTypes.bool.isRequired,
    noMatches: PropTypes.bool.isRequired,
    inputValue: PropTypes.string.isRequired,
    getMenuProps: PropTypes.func.isRequired,
    getItemProps: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
    suggestions: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            doc: PropTypes.object,
        }),
    ).isRequired,
};

export default AutocompleteResults;
